import TextField from "@mui/material/TextField";
import type { TextFieldProps } from "@mui/material/TextField";
import cn from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";

export type InputPropsType = TextFieldProps & {
  name: string;
  type?: string;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  label?: string | React.ReactNode;
  placeholder?: string;
  error?: string;
  size?: "small" | "medium";
  fullWidth?: boolean;
  className?: string;
  defaultValue?: string | number;
  required?: boolean;
  /**
   * Render as a controlled TextField driven by `value` (e.g. from a
   * react-hook-form Controller). By default the input is uncontrolled and
   * `value` only seeds the initial DOM value, so programmatic form updates
   * (setValue) are not reflected.
   */
  controlled?: boolean;
  [any: string]: any;
};

export const MuiInput = React.forwardRef((props: InputPropsType, ref: any) => {
  const {
    name,
    label,
    type = "text",
    min,
    max,
    step = "any",
    error,
    size = "medium",
    fullWidth = true,
    className,
    defaultValue,
    required,
    controlled,
    value,
    variant = "outlined",
    ...rest
  } = props;

  const { t } = useTranslation("admin");
  const id = `input-${name}`;
  const rootClasses = cn("marapp-qa-input", {
    [className]: !!className,
  });

  return (
    <>
      <TextField
        className={rootClasses}
        {...(controlled
          ? { value: value ?? "" }
          : { defaultValue: value ?? defaultValue })}
        error={!!error}
        fullWidth={fullWidth}
        // nsSeparator:false so error messages containing ":" (e.g. a URL scheme
        // like "https://") aren't parsed as a namespace delimiter. keySeparator
        // is already globally false in TranslationService.
        helperText={error && t(error, { nsSeparator: false })}
        id={id}
        name={name}
        label={typeof label === "string" ? t(label) : label}
        required={!!required}
        size={size}
        type={type}
        variant={variant}
        FormHelperTextProps={{
          className: `marapp-qa-error marapp-qa-error-${id}`,
        }}
        inputProps={{ ...(type === "number" && { min, max, step }) }}
        // Lets react-hook-form v7's `{...register(name)}` reach the native
        // input; an explicit inputRef in rest still wins.
        inputRef={ref}
        {...rest}
      />
    </>
  );
});
